import TranslationNS from '@/utils/Constants/TranslationNS'

const PLANNING = TranslationNS.PLANNING

/**
 * Page tour of « Paramètres du planning » (route `planning-editor`), top to bottom: the publish
 * switches, the workout row, the workout card (head with its ⚙ drawer and ⋯ menu as entries, the
 * fill buttons), the empty bands, the first heat (head, actions with the heat drawer as entries, bar,
 * first lane, add buttons), the « Ordre de passage » list (a left column ≥ 900 px, two buttons that
 * open it as a panel below), the save bar. Conditional blocks drop out with their anchors. Anchors
 * live in PlanningSettingsPage.jsx, WorkoutCard.jsx and HeatCard.jsx.
 */
export const PLANNING_SETTINGS_TOUR = {
    id: 'planning-settings',
    version: 1,
    paths: ['planning-editor'],
    steps: [
        {
            key: 'head',
            target: '[data-tour="page-head"]',
            placement: 'bottom',
            title: 'Planning settings',
            description:
                'Decide who competes when: workout by workout, wave by wave. The status line counts your workouts, waves and placed athletes.',
        },
        {
            key: 'preview',
            target: '[data-tour="page-preview"]',
            placement: 'bottom',
            title: 'See the public planning',
            description: 'Opens the planning as athletes see it, with what has been saved so far.',
        },
        {
            key: 'toggles',
            target: '[data-tour="plan-toggles"]',
            placement: 'bottom',
            placementNarrow: 'bottomLeft',
            title: 'Two switches, effective at once',
            description: 'They apply immediately, without saving.',
            entries: [
                { label: 'Planning published', ns: PLANNING, text: 'show the planning to athletes and the public.' },
                { label: 'Show judges', ns: PLANNING, text: 'display the judges’ names on the public planning.' },
            ],
        },
        {
            key: 'empty',
            target: '[data-tour="plan-empty"]',
            placement: 'bottom',
            title: 'Start with a workout',
            description: 'Create your first workout: the planning is built one workout at a time.',
        },
        {
            key: 'workout-row',
            target: '[data-tour="plan-workout-row"]',
            placement: 'bottom',
            placementNarrow: 'bottomLeft',
            title: 'One workout at a time',
            description:
                'Pick the workout you want to work on, or create a new one. Drag the list to change their order.',
        },
        {
            key: 'workout-head',
            target: '[data-tour="plan-workout-head"]',
            placement: 'bottom',
            title: 'The workout card',
            description: 'Its name, whether athletes can see it, and two buttons for everything else.',
            entries: [
                { label: 'Visible in the public planning', ns: PLANNING, text: 'hide a workout while you prepare it.' },
                {
                    label: 'Settings',
                    text: 'opens its drawer: visibility, date and floor, lanes and cap, pre-workout steps, deletion.',
                },
                { label: 'Actions', text: 'export the planning of athletes or judges, duplicate the workout.' },
            ],
        },
        {
            key: 'order-buttons',
            target: '[data-tour="plan-order-btns"]',
            placement: 'bottom',
            title: 'Who is left to place',
            description: 'On a small screen, these two buttons open the list of athletes and judges not yet placed.',
        },
        {
            key: 'populate',
            target: '[data-tour="plan-populate"]',
            placement: 'bottom',
            title: 'Fill the waves automatically',
            description:
                'Let the tool place everyone, then adjust by hand. The note on the right tells you what is left.',
            entries: [
                {
                    label: 'Generate the heats',
                    ns: PLANNING,
                    text: 'creates the waves from your divisions and the check-in.',
                },
                { label: 'Distribute judges', ns: PLANNING, text: 'spreads the judges over the lanes, by role.' },
                { label: 'Empty athlete lanes', ns: PLANNING, text: 'and its judges twin: start the placement again.' },
            ],
        },
        {
            key: 'no-lanes',
            target: '[data-tour="plan-nolanes"]',
            placement: 'bottom',
            title: 'Set the number of lanes first',
            description:
                'Waves need lanes. Open the workout settings and enter how many athletes compete at the same time.',
        },
        {
            key: 'no-heats',
            target: '[data-tour="plan-noheats"]',
            placement: 'bottom',
            title: 'No wave yet',
            description: 'Generate the waves from your divisions, or add one by hand and fill it yourself.',
        },
        {
            key: 'heats',
            // the first wave's title row: the wave cards and their lanes are taller than the screen
            target: '[data-tour="plan-heats"] .pln-heat',
            block: '.pln-hh',
            placement: 'bottom',
            title: 'The waves',
            description:
                'One card per wave, with its divisions. Click the start time, the duration or the number of lanes to change them.',
        },
        {
            key: 'heat-actions',
            target: '[data-tour="plan-heats"] .pln-heat [data-tour="plan-heat-acts"]',
            placement: 'bottomRight',
            placementNarrow: 'bottomLeft',
            title: 'The wave’s settings',
            description: 'The gear opens the wave’s drawer, the bin deletes it.',
            entries: [
                {
                    label: 'Schedule & lanes',
                    ns: PLANNING,
                    text: 'start, duration, lanes and transition, overriding the workout.',
                },
                { label: 'Pre-workout', ns: PLANNING, text: 'the clock times of the steps before the start.' },
                { label: 'Danger zone', ns: PLANNING, text: 'empty the wave or delete it.' },
            ],
        },
        {
            key: 'heat-bar',
            target: '[data-tour="plan-heats"] .pln-heat [data-tour="plan-heat-bar"]',
            placement: 'bottom',
            title: 'Act on the whole wave',
            description:
                'Centre the best athletes on the middle lanes, or empty the wave’s athletes or judges in one click.',
        },
        {
            key: 'lane',
            target: '[data-tour="plan-heats"] .pln-heat [data-tour="plan-lane"]',
            placement: 'bottom',
            title: 'A lane',
            description:
                'One athlete or team on the left, its judges on the right. Drop someone here, or click « Place here » after selecting them in the list. ✕ removes the lane.',
        },
        {
            key: 'add-lane',
            target: '[data-tour="plan-heats"] .pln-heat [data-tour="plan-heat-add"]',
            placement: 'bottom',
            title: 'Add a lane or a wave',
            description: 'Add a lane to this wave, or a new wave at the end of the list.',
        },
        {
            key: 'order',
            // the sticky side column is taller than the screen: point at its title band
            target: '[data-tour="plan-order"] .pln-ph1',
            placement: 'bottom',
            title: 'Who is left to place',
            description: 'The athletes and judges not yet placed. Select them and send them to the next free lane.',
            entries: [
                { label: 'Athletes / Judges order', ns: PLANNING, text: 'switch between the two lists.' },
                { label: 'Division groups', text: 'one group per division, sortable, with a « select all ».' },
                {
                    label: '{{n}} selected',
                    ns: PLANNING,
                    text: 'the bar that appears with a selection: place at the next free lane, or cancel.',
                },
                { label: 'Add a volunteer', ns: PLANNING, text: 'add a judge who is not registered yet.' },
            ],
        },
        {
            key: 'save',
            target: '[data-tour="plan-save"]',
            placement: 'top',
            title: 'Save your changes',
            description:
                'Placements are kept as a draft until you save. The line next to the button tells you what changed; « Discard changes » drops it.',
        },
    ],
}

export default PLANNING_SETTINGS_TOUR
